import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { ConfigFieldControl } from '@sierralabs/core-ui';
import {
  getRouteParams,
  SiteEventConfigProps,
  SiteEventPlugin,
  SiteEventRouteParam,
  SiteEventRouteParamConfigurator,
  SiteParamPlugin,
  useSiteContext
} from '@sierralabs/site-ui';

import { routes } from '../../environments/routes';

const StyledConfiguratorDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export interface QmsEventRouteProps {
  routePath?: string;
  routeParams?: SiteEventRouteParam[];
}

const QmsEventRouteConfigurator: React.FC<
  SiteEventConfigProps<QmsEventRouteProps>
> = props => {
  const { onChange } = props;
  const [config, setConfig] = useState<QmsEventRouteProps>(
    () => props.config || {}
  );

  const route = useMemo(() => {
    return routes.find(route => route.path === config.routePath);
  }, [config.routePath]);

  const params = useMemo(() => getRouteParams(route?.path || ''), [route]);

  return (
    <StyledConfiguratorDiv className='event-route-configurator'>
      <ConfigFieldControl>
        <Autocomplete
          fullWidth
          options={routes}
          value={route || null}
          getOptionLabel={option => `${option.label} -> ${option.path}`}
          onChange={(_, value) => {
            const routePath = value?.path || undefined;
            const newConfig = { ...config, routePath };
            setConfig(newConfig);
            onChange?.(newConfig);
          }}
          renderInput={params => (
            <TextField
              {...params}
              fullWidth
              size='small'
              variant='outlined'
              placeholder='Select Route'
            />
          )}
        />
      </ConfigFieldControl>
      {params.length > 0 && (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Param Key</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Configuration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {params.map((paramKey, index) => {
                const param = config.routeParams?.[index];
                const routeParam = param?.key === paramKey ? param : undefined;
                return (
                  <SiteEventRouteParamConfigurator
                    key={index}
                    paramKey={paramKey}
                    routeParam={routeParam}
                    onChange={routeParam => {
                      if (!config.routeParams) config.routeParams = [];
                      routeParam.key = paramKey;
                      config.routeParams[index] = routeParam;
                      const newConfig = { ...config };
                      setConfig(newConfig);
                      onChange?.(newConfig);
                    }}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </StyledConfiguratorDiv>
  );
};

export const QmsEventRoutePlugin: SiteEventPlugin<
  QmsEventRouteProps,
  {
    history: ReturnType<typeof useHistory>;
    paramPlugins: SiteParamPlugin<any>[];
  }
> = {
  name: 'QMS Route Navigation',
  type: 'qms-route-navigation',
  eventTypes: ['onClick'],
  configurator: QmsEventRouteConfigurator,
  getConfigSummary: event => {
    const route = routes.find(route => route.path === event?.config?.routePath);
    return <>Navigate to {route?.label || '?'}</>;
  },
  handler: (config, context, data?: any) => {
    const { paramPlugins, history } = context.hook;
    const route = routes.find(route => route.path === config.routePath);
    let url = route?.path || '';
    for (const routeParam of config.routeParams || []) {
      const paramPlugin = paramPlugins.find(p => p.type === routeParam.type);
      const paramContext = context.params.find(
        p => p.type === paramPlugin?.type
      );
      const value = paramPlugin?.getParamValue(
        routeParam.config,
        paramContext?.hook,
        data
      );
      url = url.replace(`:${routeParam.key}`, value);
    }
    history.push(url);
  },
  useHook: () => {
    const history = useHistory();
    const { paramPlugins } = useSiteContext();
    return { history, paramPlugins };
  }
};
